
import React, {
  Suspense,
  lazy,
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { Routes, Route, NavLink, Navigate, useLocation} from "react-router-dom";
import { useAuth } from "./context/AuthContext"; 
import BlockingOverlay from "./components/BlockingOverlay";
import AppErrorBoundary from "./components/AppErrorBoundary";
import ConfirmDialog from "./components/ConfirmDialog";
import { logger } from "./utils/logger.js";
import { playShortErrorBeep, playErrorBeep, playSuccessBeep } from "./utils/beeps";
import { ORG_ID, ROLES, SOORTEN,AFLEVER_CEL_ID, COR_CEL_ID, ACTIVE_YEAR, KG_PER_KIST, WAGEN_GROOTTE} from "./constants";
import useRijenState from "./hooks/useRijenState";
import useMachines from "./hooks/useMachines";
import { usePartij } from "./hooks/usePartij";
import useKisten from "./hooks/useKisten";
import usePlanningRijen from "./hooks/usePlanningRijen.js";
import usePercelenConfig from "./hooks/usePercelenConfig";
import { usePersistentState } from "./hooks/usePersistentState.js";
import useKoelcelConfig from "./hooks/useKoelcelConfig";
import useFustAlerts from "./hooks/useFustAlerts.js";
import { usePhoneDevice } from "./hooks/usePhoneDevice.js";
import {
  crateLookupScannerEnabled,
  upgradedWorkflowEnabled,
} from "./config/runtimeFeatures.js";
import LoginPage from "./pages/LoginPage";
import KistenAccessDeniedPage from "./pages/KistenAccessDeniedPage";
import { getMachineAlertText } from "./i18n/machineAlerts.js";
import {
   colRefs,
   getDocs, query, where, functions, httpsCallable, onSnapshot
 } from "./services/firestore";
import { buildKoelcelOptions } from "./utils/koelcelOptions";
import { isRijHandmatigBezet, rijHelpers, toJsDate } from "./utils/rijHelpers.js";
import { normalizeRijNotitie } from "./utils/rijNotitie.js";
import { heeftZelfdePartijInhoud } from "./utils/partijInhoud.js";
import { getPlanningCapacity, hasReachedPlanningCapacity } from "./utils/planningCapacity.js";
import { updateInscanPartyInfo } from "./utils/inscanPartyForm.js";
import {
  APP_PATHS,
  canRoleAccessPath,
  canRoleUsePhoneInscanner,
  getLegacyRoleDataNeeds,
  getRoleDataNeeds,
} from "./utils/roleAccess.js";
import {
  correctSecureInventory,
  deleteSecureInventory,
  setSecureCoolingRowMode,
  setSecureEmptyRowSettings,
  setSecureMachineRows,
} from "./services/secureBusinessWrites.js";
import { ToastContainer, toast } from "react-toastify";
import { INSCAN_TEXT } from "./i18n/inscanner";
import { INSCAN_EXTRA_TEXT } from "./i18n/inscannerExtras.js";
import { getNavigationText } from "./i18n/navigation.js";
import { CRATE_LOOKUP_TEXT } from "./i18n/crateLookup.js";
import "react-toastify/dist/ReactToastify.css";

const HomePage = lazy(() => import("./pages/HomePage.jsx"));
const UsersAdminPage = lazy(() => import("./pages/UsersAdminPage"));
const Historie = lazy(() => import("./pages/Historie"));
const OrderdataPage = lazy(() => import("./pages/OrderdataPage.jsx"));
const WeekOrdersPage = lazy(() => import("./pages/WeekOrdersPage.jsx"));
const PalletVoorraadPage = lazy(
  () => import("./pages/PalletVoorraadPage.jsx")
);
const PalletAssignmentScannerPage = lazy(
  () => import("./pages/PalletAssignmentScannerPage.jsx")
);
const FustVoorraadPage = lazy(
  () => import("./pages/FustVoorraadPage.jsx")
);
const PlanningPage = lazy(() => import("./pages/PlanningPage.jsx"));
const PercelenbeheerPage = lazy(
  () => import("./pages/PercelenbeheerPage.jsx")
);
const HoutenLabelsPage = lazy(
  () => import("./pages/HoutenLabelsPage")
);
const AllowedKistenPage = lazy(
  () => import("./pages/AllowedKistenPage.jsx")
);
const CrateLookupScannerPage = lazy(
  () => import("./pages/CrateLookupScannerPage.jsx")
);

const RouteFallback = () => (
  <div className="p-6" role="status" aria-live="polite">
    Laden...
  </div>
);

const SCANNER_MODE_STANDARD = "standard";
const SCANNER_MODE_PLUS = "plus";



function App() {
 const Location = useLocation();
 const {
   currentUser,
   user: firebaseUser,
   profileLoaded,
   ssoChecking,
   canScan: canScanWithHardware,
   canUitscan,
   canEdit,
   isPlanning,
   logout: logoutFromAuth,
 } = useAuth();
const role = currentUser?.role || "unknown";
 const usesManagementTheme = [
   ROLES.PLANNING,
   ROLES.SUPERVISOR,
   ROLES.ADMIN,
 ].includes(role);
 const isDevVoorraad = upgradedWorkflowEnabled;
 const isPhoneDevice = usePhoneDevice();
 const [phoneScannerMode, setPhoneScannerMode] = useState("inscan");
 const isPhoneInscanning =
   crateLookupScannerEnabled &&
   isPhoneDevice &&
   Location.pathname === APP_PATHS.CRATE_LOOKUP &&
   canRoleUsePhoneInscanner(role) &&
   phoneScannerMode === "inscan";
 const canScan = canScanWithHardware || isPhoneInscanning;
 const [scannerMode, setScannerMode] = useState(SCANNER_MODE_STANDARD);
 const dataNeeds = useMemo(
   () =>
     upgradedWorkflowEnabled
       ? getRoleDataNeeds(role, Location.pathname)
       : getLegacyRoleDataNeeds(role),
   [role, Location.pathname]
 );
 const isUitscanner = currentUser?.role === ROLES.UITSCANNER;
 const isAdmin = currentUser?.role === ROLES.ADMIN;
 const isSupervisor =
  currentUser?.role === ROLES.SUPERVISOR || isAdmin;
 const canManageStockPages = isPlanning || isAdmin;
 const canRemoveCrates = isPlanning || isAdmin;
 const isInscanner = currentUser?.role === ROLES.INSCANNER;
 const isInscannerPlus = currentUser?.role === ROLES.INSCANNER_PLUS;
 const isInscanning = isInscanner || isInscannerPlus;
 const unifiedScannerModeEnabled = isDevVoorraad && isInscanning;
 const isInscannerPlusMode = (unifiedScannerModeEnabled || isPhoneInscanning)
   ? scannerMode === SCANNER_MODE_PLUS
   : isInscannerPlus;
 const canManualInscan = isInscannerPlusMode && !isPhoneInscanning;
 const isAuthed = !!profileLoaded && !!currentUser && role !== "unknown";
 const inventoryEnabled = isAuthed && dataNeeds.inventory;
 const showBottomNav = isInscanning;
 const [inscanLang, setInscanLang] = usePersistentState(`kv_lang`, "nl");
 const [uitscanLang] = usePersistentState(`kv_lang_${ROLES.UITSCANNER}`, "nl");
 const navLang = currentUser?.role === ROLES.UITSCANNER ? uitscanLang : inscanLang;
 const navLabels = {
   ...getNavigationText(navLang),
   crateLookup: (CRATE_LOOKUP_TEXT[navLang] || CRATE_LOOKUP_TEXT.nl).nav,
 };

 useEffect(() => {
   const reloadOnMappedHardwareKey = (event) => {
     const isRefreshKey = event.key === "F5" || event.code === "F5";
     if (!isRefreshKey || event.repeat) return;

     event.preventDefault();
     event.stopPropagation();
     window.location.reload();
   };

   window.addEventListener("keydown", reloadOnMappedHardwareKey, true);
   return () =>
     window.removeEventListener("keydown", reloadOnMappedHardwareKey, true);
 }, []);
  const alertLang = currentUser?.role === ROLES.UITSCANNER ? uitscanLang : inscanLang;
  const partijFormFields = isInscannerPlusMode
  ? ["snijgroep", "perceel", "gewicht", "soort", "koelcel", "rij"]
  : ["snijgroep"];

 useEffect(() => {
   setScannerMode(SCANNER_MODE_STANDARD);
 }, [currentUser?.uid]);

 const { rijenMap, rijenKeyMap, rijenArray, gescandeKisten } =
   useRijenState({
     enabled: isAuthed,
     coolingRowsEnabled: dataNeeds.coolingRows,
     inventoryEnabled,
   });
 
 const {
  queues: planningQueues,
  isEntryCompleted: isPlanningEntryCompleted,
  markEntryCompleted: markPlanningEntryCompleted,
  addEntry: addPlanningEntry,
  removeEntry: removePlanningEntry,
  clearGroup: clearPlanningGroup,
 } = usePlanningRijen({
   enabled: isAuthed && dataNeeds.planningQueues,
 });
const planningPageState = useMemo(
  () => ({
    queues: planningQueues,
    isEntryCompleted: isPlanningEntryCompleted,
    addEntry: addPlanningEntry,
    removeEntry: removePlanningEntry,
    clearGroup: clearPlanningGroup,
  }),
  [
    planningQueues,
    isPlanningEntryCompleted,
    addPlanningEntry,
    removePlanningEntry,
    clearPlanningGroup,
  ]
);
const autoAdvanceEnabled =
  isAuthed &&
  dataNeeds.machines &&
  (role === ROLES.PLANNING || role === ROLES.UITSCANNER);

 
  
  const {machines, machineAlert, addRowToMachine, removeRowFromMachine, isAssignedToMachine, maybeAutoAdvance } = useMachines(
        { rijenMap: rijenKeyMap, isPlanning: autoAdvanceEnabled, enabled: autoAdvanceEnabled,});
  const {
    alerts: fustAlerts,
    dismissAlerts: dismissFustAlerts,
  } = useFustAlerts({
    enabled: isAuthed && dataNeeds.fustAlerts,
    userId: currentUser?.uid,
  });
  const { partijInfo, setPartijInfo, partijGestart, startNieuwePartij, beëindigPartij, isPartijInfoValid, formulierVisible, setFormulierVisible } = usePartij({
    currentUser,
    toast,
    enabled: isAuthed && dataNeeds.partySession,
  });
  const { actievePercelen, soortenPerPerceel } = usePercelenConfig({
    enabled: isAuthed && dataNeeds.parcelOptions,
  });
  const [inscanManual, setInscanManual] = usePersistentState("kv_inscan_manual", "kist-");
  const [inscanScannerValue, setInscanScannerValue] = useState("");
  const inscanScannerRef = useRef(null);
  const inscanManualRef = useRef(null);
  const [moveDraft, setMoveDraft] = usePersistentState("kv_moveDraft", null);
  const [printBatch, setPrintBatch] = usePersistentState("kv_printBatch", null);
  const [partijFormVisiblePersist, setPartijFormVisiblePersist] =
  usePersistentState("kv_partijFormVisible", false);
  const [alertOk, setAlertOk]     = useState(null);       // { barcode, message }
  const [blockingError, setBlockingError] = useState({open: false, message: "", });
  const [isBlocking, setIsBlocking] = useState(false);
  const [isFinishingPlanningRow, setIsFinishingPlanningRow] = useState(false);
  const [blockingMsg, setBlockingMsg] = useState(""); 
  const [overrideMengPartijen, setOverrideMengPartijen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRij, setModalRij] = useState(null);
  const [kistenPopupOpen, setKistenPopupOpen] = useState(false);
  const [gewichtFilterSet, setGewichtFilterSet] = useState(new Set());
  const [sortByOldest, setSortByOldest] = useState(false);
  const [activePartij] = useState(() => localStorage.getItem("activePartij") || null);
  const activePartijRef = useRef(activePartij);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const { isGemengd: modalIsGemengd, stats: modalPartijStats } =
  modalRij ? getPartijStatsForRij(modalRij, gescandeKisten) : { isGemengd: false, stats: [] };
  const modalPartijGekozen = !modalIsGemengd || !!modalRij?.geselecteerdePartijKey;
  const partijLockActive = partijGestart && canScan;
  const lastErrorSentRef = useRef(new Map()); // fingerprint -> ts

  useEffect(() => {
    if (!isInscannerPlusMode) setOverrideMengPartijen(false);
  }, [isInscannerPlusMode]);

  const { extraKoelcellen } = useKoelcelConfig({
    orgId: ORG_ID,
    enabled: isAuthed && dataNeeds.coolingCellOptions,
  });
  const koelcelOptionsUi = buildKoelcelOptions(extraKoelcellen);
 
  const it = {
    ...(INSCAN_TEXT[inscanLang] || INSCAN_TEXT.nl),
    ...(INSCAN_EXTRA_TEXT[inscanLang] || INSCAN_EXTRA_TEXT.nl),
  };
  const machineAlertText = getMachineAlertText(machineAlert, alertLang);
  const inscanLangRef = useRef(null);
  const magPlannen = isPlanning || canEdit;
  const [printerAlert, setPrinterAlert] = useState(null);

  

// ---- Globale confirm-dialog state ----
const [confirmState, setConfirmState] = useState({
  open: false,
  title: "",
  message: "",
  resolve: null,
});

// helper: toon een confirm en krijg Promise<boolean> terug
const showConfirm = React.useCallback((message, title = "Bevestigen") => {
  return new Promise((resolve) => {
    setConfirmState({
      open: true,
      title,
      message,
      resolve, // deze wordt later aangeroepen met true/false
    });
  });
}, []);

const printBatchRef = useRef(null);

useEffect(() => {
  printBatchRef.current = printBatch;
}, [printBatch]);

function makePrintBatchFromPartijInfo(info) {
  return {
    id: `pb-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    snijgroep: info?.snijgroep || "",
    perceel: info?.perceel || "",
    gewicht: info?.gewicht || "",
    soort: info?.soort || "",
    koelcel: info?.koelcel || "",
    rij: info?.rij || "",
    kisten: [],
  };
}

  const handleConfirmResult = (result) => {
  setConfirmState((prev) => {
    if (prev.resolve) {
      prev.resolve(result);
    }
    return {
      open: false,
      title: "",
      message: "",
      resolve: null,
    };
  });
};

  const handleRijVol = async (ctx) => {
  if (!ctx || !ctx.snijgroep) return;

  const currentKey = `${ctx.koelcel}-${ctx.rij}`;
  const next = findNextBeschikbarePlanningRij(ctx.snijgroep, currentKey);

  try {
    const latestKist = ctx.laatsteKist || null;
    const batchForPrint = (() => {
      if (!latestKist?.barcode) return printBatchRef.current;

      const batchInfo = {
        snijgroep: latestKist.snijgroep || ctx.snijgroep || "",
        perceel: latestKist.perceel || ctx.perceel || "",
        gewicht: latestKist.gewicht || ctx.gewicht || "",
        soort: latestKist.soort || ctx.soort || "",
        koelcel: latestKist.koelcel || ctx.koelcel || "",
        rij: latestKist.rij || ctx.rij || "",
      };

      const currentBatch = printBatchRef.current;
      const sameBatch =
        currentBatch?.id &&
        String(currentBatch.koelcel) === String(batchInfo.koelcel) &&
        Number(currentBatch.rij) === Number(batchInfo.rij) &&
        String(currentBatch.perceel) === String(batchInfo.perceel) &&
        String(currentBatch.gewicht) === String(batchInfo.gewicht) &&
        String(currentBatch.soort) === String(batchInfo.soort) &&
        String(currentBatch.snijgroep) === String(batchInfo.snijgroep);

      if (!sameBatch) {
        return {
          ...makePrintBatchFromPartijInfo(batchInfo),
          kisten: [latestKist],
        };
      }

      const alreadyInBatch = (currentBatch.kisten || []).some(
        (kist) => String(kist?.barcode || kist) === String(latestKist.barcode)
      );

      return {
        ...currentBatch,
        kisten: alreadyInBatch
          ? currentBatch.kisten || []
          : [...(currentBatch.kisten || []), latestKist],
      };
    })();

    await runBlocking(
      it.rowFullPrinting?.(currentKey) ||
        `Rij ${currentKey} is vol. Kaartjes worden geprint...`,
      async () => {
        await printBatchKaartjes(batchForPrint);
      }
    );

    await markPlanningEntryCompleted?.(ctx.snijgroep, ctx);

    if (!next) {
      setPrintBatch(null);
      toast.info(
        it.rowFullNoNext?.(currentKey, ctx.snijgroep) ||
          `Rij ${currentKey} is vol en afgerond. Er is geen volgende rij ingepland voor groep ${ctx.snijgroep}.`
      );
      return;
    }

    const nextPartijInfo = {
      ...partijInfo,
      snijgroep: ctx.snijgroep,
      perceel: next.perceel || partijInfo.perceel,
      gewicht: next.gewicht || partijInfo.gewicht,
      soort: next.soort || partijInfo.soort,
      koelcel: next.koelcel,
      rij: next.rij,
      notitie: next.notitie || "",
    };

    setPartijInfo(nextPartijInfo);
    setPrintBatch(makePrintBatchFromPartijInfo(nextPartijInfo));

    toast.success(
      it.rowAdvanced({
        currentKey,
        koelcel: next.koelcel,
        rij: next.rij,
      })
    );
  } catch (e) {
    logger.error("Print bij rij-vol mislukt", e);
    toast.error(it.printFailedNoAdvance);
  }
};

  
  const { scanKisten, uitscannen } = useKisten({
      rijenMap,
      gescandeKisten,
      partijInfo,
      partijGestart,
      currentUser,
      canScan,
      canUitscan,
      colRefs,
      toast,
      playShortErrorBeep,
      playErrorBeep,
      playSuccessBeep,
      it,
      manualModeActive: isInscannerPlusMode,
      setBlockingError,
      setAlertOk,
      overrideMengPartijen,
      onRijVol: handleRijVol,
      onKistIngescand: (kist) => {
        setPrintBatch((prev) => {
          const batchInfo = {
            snijgroep: kist?.snijgroep || partijInfo?.snijgroep || "",
            perceel: kist?.perceel || partijInfo?.perceel || "",
            gewicht: kist?.gewicht || partijInfo?.gewicht || "",
            soort: kist?.soort || partijInfo?.soort || "",
            koelcel: kist?.koelcel || partijInfo?.koelcel || "",
            rij: kist?.rij || partijInfo?.rij || "",
          };

          if (!prev?.id) {
            return {
              ...makePrintBatchFromPartijInfo(batchInfo),
              kisten: [kist],
            };
          }

          const sameBatch =
            String(prev.koelcel) === String(batchInfo.koelcel) &&
            Number(prev.rij) === Number(batchInfo.rij) &&
            String(prev.perceel) === String(batchInfo.perceel) &&
            String(prev.gewicht) === String(batchInfo.gewicht) &&
            String(prev.soort) === String(batchInfo.soort) &&
            String(prev.snijgroep) === String(batchInfo.snijgroep);

          if (!sameBatch) {
            return {
              ...makePrintBatchFromPartijInfo(batchInfo),
              kisten: [kist],
            };
          }

          return {
            ...prev,
            kisten: [...(prev.kisten || []), kist],
          };
        });
      },
      confirmFn: showConfirm,
    });

    const shouldSend = (fp) => {
      const now = Date.now();
      const prev = lastErrorSentRef.current.get(fp) || 0;
      if (now - prev < 30_000) return false;
      lastErrorSentRef.current.set(fp, now);
      return true;
    };


  useEffect(() => {
  if (!ORG_ID) return;

  const onRejection = (ev) => {
    const reason = ev?.reason;
    const fp = `unhandled:${reason?.message || String(reason) || "unknown"}`;

    if (!shouldSend(fp)) return;

    logger.error("unhandledrejection", {
      message: reason?.message || String(reason) || "Unhandled promise rejection",
      stack: reason?.stack,
      code: "unhandledrejection",
      page: window.location.pathname,
      fingerprint: fp,
    });

  };

  const onError = (ev) => {
    const err = ev?.error;
    const fp = `onerror:${err?.message || ev?.message || "unknown"}`;

    if (!shouldSend(fp)) return;

    logger.error("window.onerror", {  message: err?.message || ev?.message || "window.onerror",
      stack: err?.stack,
      code: "window.onerror",
      page: window.location.pathname,
      fingerprint: fp,
    });
  };

  window.addEventListener("unhandledrejection", onRejection);
  window.addEventListener("error", onError);

  return () => {
    window.removeEventListener("unhandledrejection", onRejection);
    window.removeEventListener("error", onError);
  };
}, []);





const formatDate = (value) => {
  const d = toJsDate(value);
  if (!d) return "-";
  return new Intl.DateTimeFormat("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
};


const toggleGewichtFilter = (g) => {
  setGewichtFilterSet(prev => {
    const next = new Set(prev);
    if (next.has(g)) next.delete(g);
    else next.add(g);
    return next;
  });
};
const clearGewichtFilter = () => setGewichtFilterSet(new Set());

// Helper om een async actie uit te voeren met blokkerende overlay
const runBlocking = async (msg, fn) => {
  setBlockingMsg(msg || it.processing || "Bezig met verwerken...");
  setIsBlocking(true);
  try {
    return await fn();
  } finally {
    setIsBlocking(false);
    setBlockingMsg("");
  }
};

const beschikbareSoortenVoorPartij =
  partijInfo.perceel && soortenPerPerceel[partijInfo.perceel]?.length
    ? soortenPerPerceel[partijInfo.perceel]
    : SOORTEN;

const isAflevercelRij = (rij) =>
  rij &&
  String(rij.koelcel).toLowerCase() === String(AFLEVER_CEL_ID).toLowerCase();

  const updateMoveDraft = (partial) => {
  if (!modalRij) return;
  setMoveDraft((prev) => ({
    rijId: modalRij.id,
    ...(prev || {}),
    ...partial,
  }));
};

useEffect(() => {
  if (!isAuthed || !canScan || !dataNeeds.partySession) {
    setPrinterAlert(null);
    return;
  }

  const ref = colRefs.printerAlertDoc(ACTIVE_YEAR);

  return onSnapshot(
    ref,
    (snap) => setPrinterAlert(snap.exists() ? snap.data() : null),
    (error) => {
      logger.warn("Printermelding kon niet worden geladen", error);
      setPrinterAlert(null);
    }
  );
}, [isAuthed, canScan, dataNeeds.partySession]);


useEffect(() => {
  activePartijRef.current = activePartij;
  if (activePartij) localStorage.setItem("activePartij", activePartij);
  else localStorage.removeItem("activePartij");
}, [activePartij]);

 // Automatisch sluiten overlays (2s)
 useEffect(() => {
   if (!alertOk) return;
   const t = setTimeout(() => setAlertOk(null), 2000);
   return () => clearTimeout(t);
 }, [alertOk]);

 // Override altijd uit als er geen partij meer loopt
useEffect(() => {
  if (!partijGestart) {
    setOverrideMengPartijen(false);
  }
}, [partijGestart]);

useEffect(() => {
  if (!partijGestart) {
    setPrintBatch(null);
  }
}, [partijGestart, setPrintBatch]);

// 1️ Scanner-keys (hardware)
useEffect(() => {
  const handler = (e) => {
    // Als je bewust handmatig typt → niets doen
    if (document.activeElement === inscanManualRef.current) return;

    // Alleen ingrijpen als scanner niet al focus heeft
    if (
      inscanScannerRef.current &&
      document.activeElement !== inscanScannerRef.current
    ) {
      inscanScannerRef.current.focus();
    }
  };

  window.addEventListener("keydown", handler, true);
  return () => window.removeEventListener("keydown", handler, true);
}, []);


// 2️ Tikken / klikken op het scherm
useEffect(() => {
  const onPointerDown = (e) => {
    // Tik in handmatige input → laten gebeuren
    if (inscanManualRef.current?.contains(e.target)) return;
    if (inscanLangRef.current?.contains(e.target)) return;

    // Handmatig veld had focus → netjes afsluiten
    if (document.activeElement === inscanManualRef.current) {
      inscanManualRef.current.blur();
    }

    // Altijd scanner-focus herstellen
    inscanScannerRef.current?.focus();
  };

  document.addEventListener("pointerdown", onPointerDown, true);
  return () =>
    document.removeEventListener("pointerdown", onPointerDown, true);
}, []);



const logout = async () => {
  // Alleen blokkeren voor mensen die kunnen scannen
  if (partijGestart && canScan) {
   const ok = await showConfirm(
    it.confirm.logoutWhileActiveMessage(),
    it.confirm.logoutWhileActiveTitle
  );


    if (!ok) return;

    try {
      // probeer eerst netjes de partij te beëindigen
      const ended = await handleBeëindigPartij({
        skipPartialWagonConfirmation: true,
      });
      if (!ended) return;
    } catch (e) {
      logger.error(e);
      toast.error("Kon partij niet beëindigen. Uitloggen afgebroken.");
      return;
    }
  }

  await logoutFromAuth();
};


 useEffect(() => {
   if (!expandedRowId) return;
   const r = rijenMap.get
     ? rijenMap.get(expandedRowId)
     : (rijenMap?.get?.(expandedRowId)); // voor de reducer-variant
   if (r && (r?.huidige_bezetting ?? 0) === 0) {
     setExpandedRowId(null);
   }
 }, [expandedRowId, rijenMap]);

useEffect(() => {
  if (!currentUser) return;

  // Laat auto-advance lopen voor planning én uitscanner
  const role = currentUser.role;
  if (role !== ROLES.PLANNING && role !== ROLES.UITSCANNER) return;

  if (!rijenMap) return;

  const run = async () => {
    try {
    
      const changes = await maybeAutoAdvance();
      if (!changes || !changes.length) return;

      // Nieuwe rows per machine wegschrijven naar Firestore
      await Promise.all(
  changes.map(async (c) => {
    const cur = machines?.[String(c.machineId)]?.rows || [];
    if (rowsEqual(cur, c.newRows)) return;          // ✅ skip no-op
    
    await setSecureMachineRows(String(c.machineId), c.newRows);
    })
      );
    } catch (e) {
      logger.error("AUTO_ADVANCE error", e);
    }
  };

  run();
}, [currentUser, rijenMap, machines, maybeAutoAdvance]);

useEffect(() => {
  if (!modalRij || !moveDraft) return;
  if (moveDraft.rijId !== modalRij.id) return; // alleen voor dezelfde rij

  setModalRij((prev) => ({
    ...prev,
    aantalTeVerplaatsen: moveDraft.aantalTeVerplaatsen ?? prev.aantalTeVerplaatsen,
    geselecteerdeKisten: moveDraft.geselecteerdeKisten ?? prev.geselecteerdeKisten,
  }));
}, [modalRij, moveDraft]);

useEffect(() => {
  // als er geen concept is, of de modal al open is, niets doen
  if (!moveDraft || modalOpen) return;

  // rijenMap kan een Map zijn; fallback op rijenArray
  let rij = null;
  if (rijenMap && rijenMap.get) {
    rij = rijenMap.get(moveDraft.rijId);
  }
  if (!rij && Array.isArray(rijenArray)) {
    rij = rijenArray.find((r) => r.id === moveDraft.rijId);
  }
  if (!rij) return;

  // modal opnieuw vullen en openen
  setModalRij(rij);
  setModalOpen(true);
}, [moveDraft, modalOpen, rijenMap, rijenArray]);

// Als er een partij loopt en het formulier stond nog open → opnieuw openen na refresh
useEffect(() => {
  if (!partijGestart) return;
  if (partijFormVisiblePersist && !formulierVisible) {
    setFormulierVisible(true);
  }
}, [partijGestart, partijFormVisiblePersist, formulierVisible, setFormulierVisible]);




// callback die je doorgeeft aan MachinesListPanel

const handleToggleMachineRow = async (machineId, rijObj) => {
  // rijObj: { koelcel, rij, warm }
  const assigned = isAssignedToMachine(machineId, rijObj);

  // ❌ Nieuwe rij toevoegen terwijl hij warm is → blokkeren
  if (!assigned && rijObj.warm) {
    toast.warn(it.warmRowCannotAssign(rijObj));
    return;
  }

  // nieuwe array laten berekenen door de hook (sync)
  const newRows = assigned
    ? removeRowFromMachine(machineId, rijObj)
    : addRowToMachine(machineId, rijObj);

  if (!newRows) return; // niets te doen

  await setSecureMachineRows(String(machineId), newRows);
};
const getPlanningEntriesForPhysicalRow = (preset) =>
  Object.values(planningQueues || {})
    .flat()
    .filter(
      (entry) =>
        String(entry?.koelcel || "") === String(preset?.koelcel || "") &&
        Number(entry?.rij || 0) === Number(preset?.rij || 0)
    );

const isPlanningPresetBeschikbaar = (snijgroep, preset) => {
  if (!preset || isPlanningEntryCompleted?.(snijgroep, preset)) return false;

  const rijObj = rijenArray.find(
    (row) =>
      String(row.koelcel) === String(preset.koelcel) &&
      Number(row.rij) === Number(preset.rij)
  );
  if (!rijObj || isRijHandmatigBezet(rijObj)) return false;

  const planningCap = getPlanningCap(rijObj.capaciteit ?? 0);
  if (Number(rijObj.huidige_bezetting || 0) >= planningCap) return false;

  const specialCoolingCell = [AFLEVER_CEL_ID, COR_CEL_ID].some(
    (cell) => String(cell).toLowerCase() === String(rijObj.koelcel).toLowerCase()
  );
  if (
    !specialCoolingCell &&
    !rijHelpers.kanRijPlaatsen(rijObj, rijenArray, 1)
  ) {
    return false;
  }

  if (Number(rijObj.huidige_bezetting || 0) === 0) return true;
  if (
    (rijObj.perceel || "") === (preset.perceel || "") &&
    (rijObj.gewicht || "") === (preset.gewicht || "") &&
    (rijObj.soort || "") === (preset.soort || "")
  ) {
    return true;
  }
  if (preset.allowMixedParties === true) return true;

  const sameRowEntries = getPlanningEntriesForPhysicalRow(preset);
  return (
    sameRowEntries.some((entry) => entry.allowMixedParties === true) &&
    sameRowEntries.some((entry) => heeftZelfdePartijInhoud(entry, rijObj))
  );
};

const findFirstBeschikbarePlanningRij = (snijgroep) =>
  (planningQueues?.[snijgroep] || []).find((preset) =>
    isPlanningPresetBeschikbaar(snijgroep, preset)
  ) || null;

const findNextBeschikbarePlanningRij = (snijgroep, currentKey) => {
  const entries = planningQueues?.[snijgroep] || [];
  const currentIndex = entries.findIndex(
    (entry) =>
      String(entry.koelcel || "") + "-" + Number(entry.rij || 0) ===
      String(currentKey)
  );
  if (currentIndex < 0) return null;

  return (
    entries
      .slice(currentIndex + 1)
      .find((preset) => isPlanningPresetBeschikbaar(snijgroep, preset)) || null
  );
};


 const handlePartijFieldChange = (field, rawValue) => {
  const key = field.toLowerCase();
  const followPlanning = !(isPhoneInscanning && isInscannerPlusMode);
  setPartijInfo(updateInscanPartyInfo({
    partyInfo: partijInfo,
    field: key,
    value: rawValue,
    followPlanning,
    planningPreset: key === "snijgroep" && followPlanning
      ? findFirstBeschikbarePlanningRij(rawValue)
      : null,
  }));
};

 const handlePhoneManualModeChange = (manual) => {
  setScannerMode(manual ? SCANNER_MODE_PLUS : SCANNER_MODE_STANDARD);
  if (manual || !partijGestart) return;
  setPartijInfo(updateInscanPartyInfo({
    partyInfo: partijInfo,
    field: "snijgroep",
    value: partijInfo.snijgroep,
    planningPreset: findFirstBeschikbarePlanningRij(partijInfo.snijgroep),
  }));
 };

function rowsEqual(a = [], b = []) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    const ra = a[i];
    const rb = b[i];
    if (!ra || !rb) return false;

    // vergelijk alleen relevante machine-row velden
    if (
      ra.koelcel !== rb.koelcel ||
      ra.rij !== rb.rij
    ) {
      return false;
    }
  }
  return true;
}

        // ---- Helper: partij-mix per rij (voor GEMENGD label) ----
        function getPartijStatsForRij(rij, gescandeKisten) {
      if (!rij?.id) return { isGemengd: false, stats: [] };

      const inDezeRij = gescandeKisten.filter((k) => k.rij_id === rij.id);
      if (!inDezeRij.length) return { isGemengd: false, stats: [] };

      const map = new Map();

      for (const k of inDezeRij) {
        const key = `${k.perceel || "-"}-${k.soort || "-"}-${k.gewicht || "-"}-${k.snijgroep || "-"}`;

        if (!map.has(key)) {
          map.set(key, {
            key,
            partij:
              k.partij ||
              `${k.perceel || "-"}-${k.soort || "-"}-${k.gewicht || "-"}-${k.snijgroep || "-"}`,
            perceel: k.perceel || "-",
            gewicht: k.gewicht || "-",
            soort: k.soort || "-",
            snijgroep: k.snijgroep || "-",
            count: 0,
            // nieuw: we bewaren zowel eerste als laatste datum
            eersteDatum: null,
            laatsteDatum: null,
          });
        }

        const g = map.get(key);
        g.count += 1;

        const d = toJsDate(k.datumIn);
        if (d) {
          // sinds wanneer staat deze partij op deze rij?
          if (!g.eersteDatum || d < g.eersteDatum) {
            g.eersteDatum = d;
          }
          // wanneer is voor het laatst een kist van deze partij gescand?
          if (!g.laatsteDatum || d > g.laatsteDatum) {
            g.laatsteDatum = d;
          }
        }
      }

      let stats = Array.from(map.values());

      //partij die korste op de rij staat als eerst
      stats.sort((a, b) => {
        const ta = a.eersteDatum ? a.eersteDatum.getTime() : 0;
        const tb = b.eersteDatum ? b.eersteDatum.getTime() : 0;
        return tb - ta; // nieuwste eerst
      });

      const isGemengd = stats.length > 1;

      return { isGemengd, stats };
    }

    const handleSetRijUitscanMode = async (rijId, mode) => {
  try {
    await setSecureCoolingRowMode(rijId, mode);
    toast.success(`Rijmodus opgeslagen: ${mode.toUpperCase()}`);
  } catch (err) {
    logger.error("uitscanMode opslaan mislukt", err);
    toast.error(
      `Opslaan van uitscanmodus mislukt: ${err?.message || "onbekende fout"}`
    );
  }
};


    const handleRemoveRow = async (machineId, rijLike) => {
      const newRows = removeRowFromMachine(machineId, rijLike);
      if (!newRows) return; // niets te wijzigen

      await setSecureMachineRows(String(machineId), newRows);
    };
    
    // --- warm-detectie (maak robuust voor je datavarianten) ---
    function isRijWarm(r) {
      try {
        return rijHelpers.getRijStatus(r) === "warm";
      } catch {
        return false;
      }
    }

    const getRijOverlay = (rij) => {
      // 🔹 Speciaal gedrag voor de aflevercel
      if (isAflevercelRij(rij)) {
        const bezet = rij.huidige_bezetting ?? 0;
        const cap = rij.capaciteit ?? 0;

        // Geen kisten → geen overlay
        if (bezet <= 0) return false;

        // Rij vol → ook geen overlay (die is “klaar”)
        if (cap > 0 && bezet >= cap) return false;

        // Alleen overlay zolang de rij nog warm is
        const status = rijHelpers.getRijStatus(rij);
        return status === "warm";
      }

      // 🔹 Alle andere koelcellen: bestaande warme-buren logica laten staan
      if (rij.huidige_bezetting > 0) return false;
      return rijHelpers.rijHeeftWarmeBuur(rij, rijenArray, null);
    };

    // helper om een consistente partij-key te maken
    function makePartijKey({ snijgroep, perceel, soort, gewicht }) {
      if (!perceel || !soort || !gewicht || !snijgroep) return null;
      return `${perceel}-${soort}-${gewicht}-${snijgroep}`;
    }

    function getNieuwsteDatumIn(kisten = []) {
      return kisten.reduce((nieuwste, kist) => {
        const datum = toJsDate(kist?.datumIn);
        if (!datum) return nieuwste;
        return !nieuwste || datum > nieuwste ? datum : nieuwste;
      }, null);
    }

    function getPartijKeyFromKist(kist = {}) {
      return `${kist.perceel || "-"}-${kist.soort || "-"}-${kist.gewicht || "-"}-${kist.snijgroep || "-"}`;
    }

    function getKistenVoorModalPartij(kisten = [], rij = modalRij) {
      const kistenOpRij = kisten.filter((k) => k.rij_id === rij?.id);
      if (modalIsGemengd && rij?.geselecteerdePartijKey) {
        return kistenOpRij.filter(
          (k) => getPartijKeyFromKist(k) === rij.geselecteerdePartijKey
        );
      }
      return kistenOpRij;
    }

    function getKgForKist(kist) {
      const kg = Number(kist?.kgPerKist);
      return Number.isFinite(kg) && kg > 0 ? kg : KG_PER_KIST;
    }

    // Aantallen per gewicht voor alle actieve kisten
      const aantallenPerGewicht = useMemo(() => {
      const map = {};
      for (const k of gescandeKisten) {
        const g = k.gewicht || "Onbekend";
        map[g] = (map[g] || 0) + 1;
      }
      return map;
      }, [gescandeKisten]);

    // --- Helper: check of een rij kisten bevat met een van de gefilterde gewichten ---
    function rowMatchesGewichtFilter(rij, gewichtFilterSet, gescandeKisten) {

      if (!gewichtFilterSet || gewichtFilterSet.size === 0) return true;

      if ((rij.huidige_bezetting ?? 0) <= 0) return false;

      const kistenInRij = gescandeKisten.filter((k) => k.rij_id === rij.id);
      if (!kistenInRij.length) return false;

      return kistenInRij.some((k) => {
        const g = k.gewicht || "Onbekend";
        return gewichtFilterSet.has(g);
      });
    }

    const getRijStyle = (rij) => {
  const status = rijHelpers.getRijStatus(rij);
  const overlay = getRijOverlay(rij);

  const baseStyle = {
    cursor: "default",
    padding: "6px",
    position: "relative",
  };

  if (isRijHandmatigBezet(rij)) {
    return {
      ...baseStyle,
      backgroundColor: "#f3f4f6",
      border: "2px solid #4b5563",
      color: "#111827",
      overflow: "hidden",
    };
  }

  if (overlay) {
    return { ...baseStyle, backgroundColor: "rgba(255,0,0,0.2)" };
  }

  const clamp01 = (x) => Math.max(0, Math.min(1, x));

  // 🔹 3 weken cap (21 dagen)
  const MAX_DAYS = 21;
  const MAX_MS = MAX_DAYS * 24 * 60 * 60 * 1000;

  // 🔹 Startmoment “koud”: datumIn + KOELTIJD_SECONDEN
  // (datumIn is bij jou de start van warm; zodra KOELTIJD voorbij is, is rij “koud”.)
 const dIn = toJsDate(rij?.datumIn);

// Alleen rekenen als de rij echt gekoeld is
const coldSinceMs =
  status === "gekoeld" && dIn
    ? dIn.getTime()
    : null;


  const ageMs = coldSinceMs ? Date.now() - coldSinceMs : 0;
  const t = clamp01(ageMs / MAX_MS) * 0.75;

 // --- Groen verloop gebaseerd op origineel groen (#22c55e) ---
// #22c55e ≈ hsl(142, 71%, 45%)

const HUE = 142;
const SAT = 71;

// Start iets lichter dan origineel
const START_L = 55;

// Eindigt donker groen
const END_L = 28;

// t loopt al van 0 → 1 (of 0 → 0.5 als je dat eerder zo instelde)
const lightness = START_L - t * (START_L - END_L);
const gekoeldBg = `hsl(${HUE}, ${SAT}%, ${lightness}%)`;

// Leesbare tekstkleur
const gekoeldText = "white";


  // ---- bestaande kleurkeuzes ----
  let bgColor;
  let textColor = "white";
  const visueelVolCap =
    (String(rij?.koelcel) === "4" || String(rij?.koelcel) === "5") &&
    Number(rij?.capaciteit ?? 0) === 32
      ? 28
      : Number(rij?.capaciteit ?? 0);

  if (Number(rij?.huidige_bezetting ?? 0) >= visueelVolCap) {
    if (status === "gekoeld") {
      bgColor = gekoeldBg;
      textColor = gekoeldText;
    } else {
      bgColor = "#f87171";
      textColor = "white";
    }

    return {
      ...baseStyle,
      background: `linear-gradient(to right, ${bgColor} 50%, black 50%)`,
      color: textColor,
    };
  }

  if (status) {
    if (status === "gekoeld") {
      bgColor = gekoeldBg;
      textColor = gekoeldText;
    } else {
      bgColor = "#f87171";
      textColor = "white";
    }

    return {
      ...baseStyle,
      backgroundColor: bgColor,
      color: textColor,
      cursor: "default",
    };
  }

  return { ...baseStyle, backgroundColor: "#e5e7eb" };
};


    const handleStartNieuwePartij = () => {
      // reset lijst kisten van deze partij
      setPrintBatch(null);

      // formulier moet open zijn als je een nieuwe partij start
      setPartijFormVisiblePersist(true);

      // bestaande logica uit hook
      startNieuwePartij();
    };
 
    const printBatchKaartjes = async (batch) => {
      if (!batch?.kisten?.length) return true;

      const kisten = batch.kisten || [];
      const onlyHoutenKisten = kisten.every((k) => {
        const type = String(
          k?.type || k?.kistType || ""
        ).toLowerCase();

        return type === "houtenkist" || type === "hout";
      });
      // Voor houten kisten geen extra kistkaart printen
      if (onlyHoutenKisten) {
        return true;
      }

      const job = {
        type: "kistkaart",
        paper: "A5",
        copiesPerItem: 2,
        count: 1,
        sourceCrateCount: kisten.length,
        partijInfo: {
          snijgroep: batch.snijgroep || "",
          perceel: batch.perceel || "",
          gewicht: batch.gewicht || "",
          soort: batch.soort || "",
          koelcel: batch.koelcel || "",
          rij: batch.rij || "",
        },
      };

      const createPrintJob = httpsCallable(functions, "createPrintJob");

      const requestId =
        batch.id
          ? `kistkaart-${batch.id}`
          : typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `kistkaart-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      await createPrintJob({
        orgId: ORG_ID,
        year: String(ACTIVE_YEAR),
        job,
        requestId,
      });

      return true;
    };



   const handleBeëindigPartij = async (options = {}) => {
      const activePrintBatch = printBatchRef.current;
      const scannedCrateCount = activePrintBatch?.kisten?.length || 0;
      const skipPartialWagonConfirmation =
        options?.skipPartialWagonConfirmation === true;

      if (
        !skipPartialWagonConfirmation &&
        scannedCrateCount > 0 &&
        scannedCrateCount < WAGEN_GROOTTE
      ) {
        const confirmed = await showConfirm(
          it.confirm.partialWagonMessage({
            count: scannedCrateCount,
            wagonSize: WAGEN_GROOTTE,
          }),
          it.confirm.partialWagonTitle({ count: scannedCrateCount })
        );
        if (!confirmed) return false;
      }

      try {
        if (activePrintBatch?.kisten?.length) {
          setIsBlocking(true);
          setBlockingMsg(it.printingCrateCards || "Kistkaarten worden geprint...");

         await printBatchKaartjes(activePrintBatch);
        }

        const activePlanningEntry = (
          planningQueues?.[partijInfo?.snijgroep] || []
        ).find(
          (entry) =>
            String(entry?.koelcel || "") === String(partijInfo?.koelcel || "") &&
            Number(entry?.rij || 0) === Number(partijInfo?.rij || 0) &&
            heeftZelfdePartijInhoud(entry, partijInfo) &&
            !isPlanningEntryCompleted?.(partijInfo?.snijgroep, entry)
        );
        const activePlanningRow = rijenArray.find(
          (row) =>
            String(row?.koelcel || "") === String(partijInfo?.koelcel || "") &&
            Number(row?.rij || 0) === Number(partijInfo?.rij || 0)
        );

        if (
          activePlanningEntry &&
          hasReachedPlanningCapacity(activePlanningRow)
        ) {
          await markPlanningEntryCompleted?.(
            partijInfo.snijgroep,
            activePlanningEntry
          );
        }

        setPrintBatch(null);
        setPartijFormVisiblePersist(false);
        await beëindigPartij();
        return true;
      } catch (e) {
        logger.error(e);
        toast.error("Printopdracht maken mislukt. Partij is niet beëindigd.");
        return false;
      } finally {
        setIsBlocking(false);
        setBlockingMsg("");
      }
    };


    const getPlanningCap = getPlanningCapacity;

    const planningAdvancePreview = (() => {
      if (!canScan || !partijGestart || formulierVisible) return null;

      const snijgroep = partijInfo?.snijgroep;
      const currentKey = `${partijInfo?.koelcel || ""}-${Number(partijInfo?.rij || 0)}`;
      if (!snijgroep || currentKey.endsWith("-0")) return null;

      const list = planningQueues?.[snijgroep] || [];
      const currentEntry = list.find(
        (entry) =>
          `${String(entry.koelcel || "")}-${Number(entry.rij || 0)}` === currentKey &&
          !isPlanningEntryCompleted?.(snijgroep, entry)
      );
      if (!currentEntry) return null;

      const next = findNextBeschikbarePlanningRij(snijgroep, currentKey);
      if (!next) return null;

      const hasDifferentPartijInfo = ["perceel", "gewicht", "soort"].some(
        (field) => String(next?.[field] || "") !== String(partijInfo?.[field] || "")
      );
      if (!hasDifferentPartijInfo) return null;

      const currentRow = rijenMap?.get?.(currentKey);

      return {
        current: {
          ...currentEntry,
          huidige_bezetting: Number(currentRow?.huidige_bezetting || 0),
        },
        next,
      };
    })();

    const handleFinishPlanningRow = async () => {
      if (!planningAdvancePreview || isFinishingPlanningRow) return;

      const { current, next } = planningAdvancePreview;
      const currentKey = `${current.koelcel}-${current.rij}`;
      const snijgroep = current.snijgroep || partijInfo.snijgroep;

      setIsFinishingPlanningRow(true);
      try {
        await runBlocking(
          it.finishRowPrinting(currentKey),
          async () => {
            await printBatchKaartjes(printBatchRef.current);
            await markPlanningEntryCompleted?.(snijgroep, current);
          }
        );

        const nextPartijInfo = {
          ...partijInfo,
          snijgroep,
          perceel: next.perceel || partijInfo.perceel,
          gewicht: next.gewicht || partijInfo.gewicht,
          soort: next.soort || partijInfo.soort,
          koelcel: next.koelcel,
          rij: next.rij,
          notitie: next.notitie || "",
        };

        setPartijInfo(nextPartijInfo);
        setPrintBatch(makePrintBatchFromPartijInfo(nextPartijInfo));

        toast.success(
          it.finishRowDone({
            currentKey,
            koelcel: next.koelcel,
            rij: next.rij,
          })
        );
      } catch (e) {
        logger.error("Rij handmatig afronden mislukt", e);
        toast.error(it.finishRowFailed);
      } finally {
        setIsFinishingPlanningRow(false);
      }
    };

    const getBeschikbareRijenDropdown = (rijen, koelcel, motor, partijInfo, excludeRijId = null, kistToevoegen = 1) => {
      const isSpecialKoelcel = (koelcel) => {
      const v = String(koelcel || "").toLowerCase();
      return (
        v === String(AFLEVER_CEL_ID).toLowerCase() ||
        v === String(COR_CEL_ID).toLowerCase()
      );
    };
      // Bepaal of we iets verplaatsen vanuit een bronrij die warm is (bij modal-verplaatsing)

      const sourceRow = excludeRijId 
      ? rijen.find((r) => r.id === excludeRijId) : null;
      const sourceIsWarm = sourceRow ? rijHelpers.getRijStatus(sourceRow) === "warm" : false;

      return rijen.filter((rij) => {
      if (rij.koelcel !== koelcel) return false;
      if (isRijHandmatigBezet(rij)) return false;
      const cap = rij.capaciteit ?? 0;
      const bezet = rij.huidige_bezetting ?? 0;

      if (bezet >= cap) return false;
      if (excludeRijId && rij.id === excludeRijId) return false;

      // 🔴 Meng-verbod alléén op basis van perceel/soort/gewicht
      if (!overrideMengPartijen && rij.partij) {
        const zelfdePerceel  = (rij.perceel  || "") === (partijInfo.perceel  || "");
        const zelfdeSoort    = (rij.soort    || "") === (partijInfo.soort    || "");
        const zelfdeGewicht  = (rij.gewicht  || "") === (partijInfo.gewicht  || "");

        // Als één van die drie anders is → andere partij → blokkeren
        if (!zelfdePerceel || !zelfdeSoort || !zelfdeGewicht) {
          return false;
        }
      }

      const effToevoegen = excludeRijId
        ? (sourceIsWarm ? kistToevoegen : 0)
        : kistToevoegen;

      if (!isSpecialKoelcel(rij.koelcel)) {
      return rijHelpers.kanRijPlaatsen(
        rij,
        rijen,
        effToevoegen,
        excludeRijId
      );
    }
      // aflevercel/cor: altijd toestaan qua warme buur (anderen checks blijven gelden)
      return true;
    });


    };

    const openModalFunc = (rij) => {
      if (!(canEdit || isPlanning)) return;
      const isLegeRij = (rij?.huidige_bezetting ?? 0) <= 0 && !rij?.partij;
      if (isLegeRij) {
        setModalRij({
          ...rij,
          isEmptyRowSettings: true,
          handmatigBezet: isRijHandmatigBezet(rij),
          notitie: rij.notitie || "",
        });
        setModalOpen(true);
        return;
      }

      if (!rij.partij) return;

      // Alle kisten op deze rij ophalen
      const kistenOpRij = gescandeKisten.filter((k) => k.rij_id === rij.id);
      const aantal = kistenOpRij.length;

      // Snijgroep bepalen
      // 1) als rij al snijgroep heeft → gebruik die
      // 2) anders → gebruik snijgroep van eerste kist
      let snijgroep = rij.snijgroep || "";
      if (!snijgroep && kistenOpRij.length > 0) {
        snijgroep = kistenOpRij[0].snijgroep || "";
      }

      // Perceel / Soort / Gewicht hetzelfde principe
      let perceel = rij.perceel || "";
      let soort = rij.soort || "";
      let gewicht = rij.gewicht || "";

      if (kistenOpRij.length > 0) {
        const k = kistenOpRij[0];
        if (!perceel) perceel = k.perceel || "";
        if (!soort) soort = k.soort || "";
        if (!gewicht) gewicht = k.gewicht || "";
      }

      setModalRij({
        ...rij,
        snijgroep,
        perceel,
        soort,
        gewicht,
        notitie: rij.notitie || "",
        uitscanMode: rij.uitscanMode || "all",
        aantalTeVerplaatsen: aantal,
        geselecteerdeKisten: [],  
      });

      setModalOpen(true);
    };



      const closeModal = () => {
        setModalOpen(false);
        setModalRij(null);
        setOverrideMengPartijen(false);
        setKistenPopupOpen(false);
        setMoveDraft(null);
      };

    const saveEmptyRowSettings = async (rij) => {
      if (!(canEdit || isPlanning) || !rij?.id) {
        toast.error(it.editNoRights);
        return;
      }

      const actueleRij = rijenMap.get(rij.id);
      if (!actueleRij) {
        toast.error(it.rowNotFoundShort);
        return;
      }

      if ((actueleRij.huidige_bezetting ?? 0) > 0 || actueleRij.partij) {
        toast.warn(it.emptyRowsOnly);
        return;
      }

      const handmatigBezet = rij.handmatigBezet === true;
      const nieuweNotitie = handmatigBezet ? normalizeRijNotitie(rij.notitie) : null;
      try {
        await setSecureEmptyRowSettings(
          rij.id,
          handmatigBezet,
          nieuweNotitie
        );
        toast.success(it.rowOccupancySaved);
        closeModal();
      } catch (err) {
        logger.error("Lege rij bezet-status opslaan mislukt", err);
        toast.error(it.saveDatabaseFailed);
      }
    };

    const isModalValid = (rij) => {
      if (!rij) return false;

      // verplicht:
      const verplicht = ["snijgroep", "perceel", "gewicht", "soort", "koelcel", "rij"];

      for (const f of verplicht) {
        if (!rij[f] || rij[f] === "") return false;
      }

      // gemengde rij → partij moet gekozen zijn
      if (modalIsGemengd && !rij.geselecteerdePartijKey) return false;

      // minimaal 1 kist
      const aantal = Number(rij.aantalTeVerplaatsen || 0);
      if (isNaN(aantal) || aantal <= 0) return false;

      // 👉 alleen extra check als de rij echt verplaatst wordt
      const origineleRij = rijenMap.get(rij.id);
      const rijVerplaatst =
        !!origineleRij &&
        (origineleRij.koelcel !== rij.koelcel || origineleRij.rij !== rij.rij);

      if (!rijVerplaatst) {
        // alleen eigenschappen aanpassen → selectie van individuele kisten niet verplicht
        return true;
      }

      // Bepaal hoeveel kisten er op deze rij voor deze partij staan
      const alleKistenOpRij = gescandeKisten.filter((k) => k.rij_id === rij.id);
      let relevanteKisten = alleKistenOpRij;

      if (modalIsGemengd && rij.geselecteerdePartijKey) {
        relevanteKisten = alleKistenOpRij.filter((k) => {
          const key = `${k.perceel || "-"}-${k.soort || "-"}-${k.gewicht || "-"}-${
            k.snijgroep || "-"
          }`;
          return key === rij.geselecteerdePartijKey;
        });
      }

      const totaalBeschikbaar = relevanteKisten.length;

      // Als we niet alle kisten verplaatsen → expliciete selectie verplicht
      if (totaalBeschikbaar > 0 && aantal < totaalBeschikbaar) {
        const gekozen = rij.geselecteerdeKisten || [];
        if (gekozen.length !== aantal) return false;
      }

      return true;
    };


    const saveModal = async (aantalTeVerplaatsen) => {
      if (!(canEdit || isPlanning)) {
        toast.error(it.editNoRights);
        return;
      }
      if (!modalRij) return;
      
      if (!modalRij.soort || modalRij.soort === "Selecteer") {
        alert(it.chooseVarietyBeforeSave);
        return;
      }

      
      // 0) Zorg dat we met een getal werken
      const aantal = Number(aantalTeVerplaatsen) || 0;
      if (aantal <= 0) {
        toast.warn(it.selectAtLeastOneCrate);
        return;
      }

      // 1) Basis: huidige rijen uit de map halen
      const updatedRijen = Array.from(rijenMap.values());
      const origineleRijIndex = updatedRijen.findIndex((r) => r.id === modalRij.id);
      if (origineleRijIndex === -1) {
        toast.error(it.originalRowNotFound);
        return;
      }

      const origineleRij = updatedRijen[origineleRijIndex];

      const rijVerplaatst =
        origineleRij.koelcel !== modalRij.koelcel ||
        origineleRij.rij !== modalRij.rij;

      const nieuwePartijNaam = makePartijKey({
      snijgroep: modalRij.snijgroep,
      perceel: modalRij.perceel,
      soort: modalRij.soort,
      gewicht: modalRij.gewicht,
    });
      const nieuweNotitie = normalizeRijNotitie(modalRij.notitie);

      let doelRij = origineleRij;

      // 2) Doelrij bepalen + checks
      if (rijVerplaatst) {
        doelRij = updatedRijen.find(
          (r) => r.koelcel === modalRij.koelcel && r.rij === modalRij.rij
        );

        if (!doelRij) {
          toast.error(it.newRowNotFound);
          return;
        }

        if (isRijHandmatigBezet(doelRij)) {
          toast.warn(
            it.errors?.rowManuallyOccupied?.({
              koelcel: doelRij.koelcel,
              rij: doelRij.rij,
            }) || "Deze rij is handmatig als bezet gemarkeerd."
          );
          return;
        }

        // Capaciteit check
        if ((doelRij.huidige_bezetting || 0) + aantal > doelRij.capaciteit) {
          toast.warn(it.rowNotEnoughSpace(doelRij));
          return;
        }

        // Warme-buren check
        const isAflevercelRijObj = (r) =>
          r && String(r.koelcel).toLowerCase() === String(AFLEVER_CEL_ID).toLowerCase();

        const sourceIsAflevercel = isAflevercelRijObj(origineleRij);
        const targetIsAflevercel = isAflevercelRijObj(doelRij);

        // Alleen warme-buur check als bron en doel niet in aflevercel zitten
        if (!sourceIsAflevercel && !targetIsAflevercel) {
          const sourceIsWarm = rijHelpers.getRijStatus(origineleRij) === "warm";
          const effToevoegen = sourceIsWarm ? aantal : 0;
        if (
          !rijHelpers.kanRijPlaatsen(
            doelRij,
            updatedRijen,          // bestaande rijen gebruiken
            effToevoegen,
            modalRij.id
          )
        ) {
          toast.warn(it.cannotMoveCratesToRow);
          return;
        }
        }
      }

      // 3) Firestore: kist-documenten bijwerken
      let movedCount = 0;
      let bronDatumInNaVerplaatsen = null;
      let doelDatumInNaVerplaatsen = null;
      try {
        const qK = query(
      colRefs.kisten(),
      where("rij_id", "==", origineleRij.id)
    );
    const snap = await getDocs(qK);

    // 🔑 Bepaal welke partij gekozen is in het modal (bij gemengde rij)
    const geselecteerdKey = modalRij.geselecteerdePartijKey || null;

    // Filter docs: alleen kisten van de gekozen partij (als er een gekozen is)
    // Anders: alle kisten (niet-gemengde rij)
    let candidateDocs = snap.docs;
    if (geselecteerdKey) {
      candidateDocs = snap.docs.filter((d) => {
        const data = d.data();
        const key = `${data.perceel || "-"}-${data.soort || "-"}-${data.gewicht || "-"}-${data.snijgroep || "-"}`;
        return key === geselecteerdKey;
      });
    }

    // Safety: geen matches → stop
    if (!candidateDocs.length) {
      toast.warn(it.noCratesForSelectedPartyOnRow);
      return;
    }

    // Te bewerken/verplaatsen docs bepalen
    let teVerplaatsenDocs;

    if (rijVerplaatst) {
      const totaalBeschikbaar = candidateDocs.length;
      const gekozenBarcodes = modalRij.geselecteerdeKisten || [];

      if (totaalBeschikbaar > 0 && aantal < totaalBeschikbaar) {
        // 🔹 Gebruiker heeft specifieke kisten gekozen (partial move)
        teVerplaatsenDocs = candidateDocs.filter((d) =>
          gekozenBarcodes.includes(d.id)
        );

        if (teVerplaatsenDocs.length !== aantal) {
          toast.error(
            it.selectedCratesMismatch
          );
          return;
        }
      } else {
        // 🔹 Alle kisten van deze partij op de rij verplaatsen
        teVerplaatsenDocs = candidateDocs;
      }
    } else {
      // zelfde rij: wijzig alle kisten van deze partij
      teVerplaatsenDocs = candidateDocs;
    }

    // werkelijk aantal verplaatste/bewerkte kisten
    movedCount = teVerplaatsenDocs.length;

    if (rijVerplaatst) {
      const teVerplaatsenIds = new Set(teVerplaatsenDocs.map((d) => d.id));
      const resterendeBronKisten = snap.docs
        .filter((d) => !teVerplaatsenIds.has(d.id))
        .map((d) => ({ barcode: d.id, ...d.data() }));
      const verplaatsteKisten = teVerplaatsenDocs.map((d) => ({
        barcode: d.id,
        ...d.data(),
      }));
      const bestaandeDoelKisten = gescandeKisten.filter(
        (k) => k.rij_id === doelRij.id
      );

      bronDatumInNaVerplaatsen = getNieuwsteDatumIn(resterendeBronKisten);
      doelDatumInNaVerplaatsen = getNieuwsteDatumIn([
        ...bestaandeDoelKisten,
        ...verplaatsteKisten,
      ]);
    }


    const correctionResult = await correctSecureInventory({
      sourceRowId: origineleRij.id,
      barcodes: teVerplaatsenDocs.map((entry) => entry.id),
      target: {
        snijgroep: modalRij.snijgroep,
        perceel: modalRij.perceel,
        gewicht: modalRij.gewicht,
        soort: modalRij.soort,
        koelcel: modalRij.koelcel,
        rij: modalRij.rij,
        notitie: nieuweNotitie,
        uitscanMode: modalRij.uitscanMode || "all",
      },
    });
    movedCount = Number(correctionResult?.movedCount || teVerplaatsenDocs.length);

      } catch (err) {
        logger.error("Fout bij updaten van kisten in Firestore", err);
        toast.error(it.saveDatabaseFailed);
        return; // niet verder gaan als Firestore faalt
      }

      // 4) Rijen-tabel bijwerken (state/koeling.rijen)
      if (rijVerplaatst) {
        // bronrij omlaag tellen
        const nieuwBronAantal = Math.max(
          0,
          (origineleRij.huidige_bezetting || 0) - movedCount
        );

        const nieuweBronRij = {
          ...origineleRij,
          huidige_bezetting: nieuwBronAantal,
        };

        // Als bronrij leegvalt → metadata resetten
        if (nieuwBronAantal <= 0) {
          nieuweBronRij.partij = null;
          nieuweBronRij.datumIn = null;
          nieuweBronRij.snijgroep = null;
          nieuweBronRij.perceel = null;
          nieuweBronRij.gewicht = null;
          nieuweBronRij.soort = null;
          nieuweBronRij.notitie = null;
          nieuweBronRij.uitscanMode = "all";
        } else {
          nieuweBronRij.datumIn = bronDatumInNaVerplaatsen || nieuweBronRij.datumIn;
        }

        // doelrij omhoog tellen
      const doelIndex = updatedRijen.findIndex((r) => r.id === doelRij.id);
    const wasLeeg = (doelRij.huidige_bezetting || 0) === 0;

    const nieuweDoelRij = {
      ...doelRij,
      huidige_bezetting: (doelRij.huidige_bezetting || 0) + movedCount,
      datumIn: doelDatumInNaVerplaatsen || doelRij.datumIn || origineleRij.datumIn,
      notitie: nieuweNotitie,
       uitscanMode: modalRij.uitscanMode || doelRij.uitscanMode || "all",
    };

    // Alleen als de doelrij leeg was, mag deze partij de "kop" van de rij worden
    if (wasLeeg) {
      nieuweDoelRij.partij = nieuwePartijNaam;
      nieuweDoelRij.snijgroep = modalRij.snijgroep;
      nieuweDoelRij.perceel = modalRij.perceel;
      nieuweDoelRij.gewicht = modalRij.gewicht;
      nieuweDoelRij.soort = modalRij.soort;
    }

    updatedRijen[origineleRijIndex] = nieuweBronRij;
    updatedRijen[doelIndex] = nieuweDoelRij;

      } else {
        // Zelfde rij: alleen eigenschappen wijzigen op de rij
        updatedRijen[origineleRijIndex] = {
          ...origineleRij,
          partij: nieuwePartijNaam,
          snijgroep: modalRij.snijgroep,
          perceel: modalRij.perceel,
          gewicht: modalRij.gewicht,
          soort: modalRij.soort,
          notitie: nieuweNotitie,
           uitscanMode: modalRij.uitscanMode || "all",
          // bezetting blijft gelijk
        };
      }

      // 5) Bewaar rijen in state/koeling → overview krijgt de nieuwe stand
      toast.success(rijVerplaatst ? it.cratesMoved : it.propertiesUpdated);
      setMoveDraft(null);
      closeModal();
    };

    const deleteSelectedModalKisten = async (mode) => {
      if (!canRemoveCrates) {
        toast.error(it.deleteNoRights);
        return;
      }

      if (!modalRij) return;
      if (modalIsGemengd && !modalRij.geselecteerdePartijKey) {
        toast.warn(it.choosePartyToDelete);
        return;
      }

      const selectedKisten = getKistenVoorModalPartij(gescandeKisten, modalRij);
      const aantal = selectedKisten.length;

      if (aantal <= 0) {
        toast.warn(it.noCratesForParty);
        return;
      }

      const isTroep = mode === "troep";
      const actieLabel = isTroep ? it.trashActionLabel : it.missedScanoutActionLabel;
      const partijLabel = it.deletePartyLabel({
        group: modalRij.snijgroep,
        perceel: modalRij.perceel,
        gewicht: modalRij.gewicht,
        soort: modalRij.soort,
      });
      const totalKg = selectedKisten.reduce((sum, kist) => sum + getKgForKist(kist), 0);

      const confirmed = await showConfirm(
        it.deleteConfirmMessage({
          count: aantal,
          partyLabel: partijLabel,
          koelcel: modalRij.koelcel,
          rij: modalRij.rij,
          isTrash: isTroep,
          totalKg,
        }),
        actieLabel
      );

      if (!confirmed) return;

      const selectedBarcodes = Array.from(new Set(
        selectedKisten.map((k) => k.barcode || k.id).filter(Boolean)
      ));

      try {
        await deleteSecureInventory({
          rowId: modalRij.id,
          barcodes: selectedBarcodes,
          mode,
        });

        toast.success(it.deleteSuccess(aantal));
        closeModal();
      } catch (err) {
        logger.error("Kisten verwijderen mislukt", err);
        toast.error(it.deleteFailed);
      }
    };

    const homePageProps = {
      activePartijRef,
      actievePercelen,
      aantallenPerGewicht,
      beschikbareSoortenVoorPartij,
      canEdit,
      canManualInscan,
      canRemoveCrates,
      canScan,
      clearGewichtFilter,
      currentUser,
      expandedRowId,
      formatDate,
      formulierVisible,
      gescandeKisten,
      getBeschikbareRijenDropdown,
      getPartijStatsForRij,
      getPlanningCap,
      getRijStyle,
      gewichtFilterSet,
      inscanLang,
      inscanLangRef,
      inscanManual,
      inscanManualRef,
      inscanScannerRef,
      inscanScannerValue,
      isAssignedToMachine,
      isBlocking,
      isDevVoorraad,
      isFinishingPlanningRow,
      isInscanner: isInscanning,
      isInscannerPlusMode,
      isModalValid,
      isPartijInfoValid,
      isPlanning,
      isRijWarm,
      it,
      kistenPopupOpen,
      koelcelOptionsUi,
      machines,
      magPlannen,
      modalIsGemengd,
      modalOpen,
      modalPartijGekozen,
      modalPartijStats,
      modalRij,
      onCloseModal: closeModal,
      onEndPartij: handleBeëindigPartij,
      onFinishPlanningRow: handleFinishPlanningRow,
      onOpenModal: openModalFunc,
      onPartijFieldChange: handlePartijFieldChange,
      onRemoveRow: handleRemoveRow,
      onDeleteSelectedKisten: deleteSelectedModalKisten,
      onSaveEmptyRowSettings: saveEmptyRowSettings,
      onSaveModal: saveModal,
      onSetRijUitscanMode: handleSetRijUitscanMode,
      onStartNieuwePartij: handleStartNieuwePartij,
      onToggleMachineRow: handleToggleMachineRow,
      orgId: ORG_ID,
      overrideMengPartijen,
      partijFormFields,
      partijGestart,
      partijInfo,
      planningAdvancePreview,
      performUitscan: uitscannen,
      playShortErrorBeep,
      printBatch,
      rijenArray,
      rijenKeyMap,
      rijenMap,
      rowMatchesGewichtFilter,
      scanKisten,
      setAlertOk,
      setBlockingError,
      setExpandedRowId,
      setFormulierVisible,
      setInscanLang,
      setInscanManual,
      setInscanScannerValue,
      setKistenPopupOpen,
      setModalRij,
      setOverrideMengPartijen,
      setPartijFormVisiblePersist,
      setSortByOldest,
      showBottomNav,
      soortenPerPerceel,
      sortByOldest,
      toggleGewichtFilter,
      updateMoveDraft,
    };


    return (

        <div
          className={`${usesManagementTheme ? "management-shell" : ""} min-h-screen bg-green-50 ${
            isUitscanner ? "p-3 md:p-6" : "p-6"
          }`}
        >
          <BlockingOverlay
              open={isBlocking || blockingError.open}
              title={it.warning || "LET OP!!"}
              closeLabel={it.closing || "Sluiten"}
              topOffset={blockingError.open ? blockingError.topOffset : null}
              message={
                blockingError.open
                  ? blockingError.message
                  : blockingMsg
              }
              lang={currentUser?.role === ROLES.UITSCANNER ? uitscanLang : inscanLang}
              onClose={() => {
                if (blockingError.open) {
                  setBlockingError({ open: false, message: "" });
                  return;
                }

                if (isBlocking) {
                  setIsBlocking(false);
                  setBlockingMsg("");
                }
              }}
            />
          <ConfirmDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={it.confirm.yes}
          cancelLabel={it.confirm.no}
          onConfirm={() => handleConfirmResult(true)}
          onCancel={() => handleConfirmResult(false)}
          />

          {((isAuthed && canScan && printerAlert) ||
            (isPlanning && machineAlert) ||
            fustAlerts.length > 0) && (
            <div
              style={{
                position: "sticky",
                top: 0,
                zIndex: 9999,
              }}
            >
              {isAuthed && canScan && printerAlert && (
                <div
                  style={{
                    padding: "10px 12px",
                    background: "#d32f2f",
                    color: "white",
                    fontWeight: 700,
                    borderBottom: "2px solid rgba(255,255,255,0.25)",
                  }}
                >
                  ⚠: {printerAlert.message || "Onbekende printerfout"}
                </div>
              )}

              {isPlanning && machineAlert && (
                <div
                  style={{
                    padding: "10px 12px",
                    background: "#d32f2f",
                    color: "white",
                    fontWeight: 700,
                    borderBottom: "2px solid rgba(255,255,255,0.25)",
                    whiteSpace: "pre-line",
                  }}
                >
                  ⚠: {machineAlertText}
                </div>
              )}

              {fustAlerts.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 16,
                    padding: "10px 12px",
                    background: "#d32f2f",
                    color: "white",
                    fontWeight: 700,
                    borderBottom: "2px solid rgba(255,255,255,0.25)",
                  }}
                >
                  <div>
                    <div>⚠ Fustvoorraad bijna op</div>
                    {fustAlerts.map((alert) => (
                      <div key={alert.alertKey} style={{ marginTop: 4, fontWeight: 600 }}>
                        {alert.threshold === 0
                          ? `${alert.type}: theoretisch tekort van ${Math.abs(alert.theoretical).toLocaleString("nl-NL")} ${alert.unit}.`
                          : `${alert.type}: theoretisch nog ${alert.theoretical.toLocaleString("nl-NL")} ${alert.unit} (marge: ${alert.threshold.toLocaleString("nl-NL")}).`}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={dismissFustAlerts}
                    aria-label="Fustwaarschuwing sluiten"
                    style={{
                      flex: "0 0 auto",
                      border: "1px solid rgba(255,255,255,0.8)",
                      borderRadius: 6,
                      padding: "4px 9px",
                      background: "rgba(255,255,255,0.15)",
                      color: "white",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Sluiten ×
                  </button>
                </div>
              )}
            </div>
          )}

          {alertOk && (
            <div style={{position:'fixed',inset:0,background:'rgba(46,204,113,0.10)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9998}}>
              <div style={{background:'#fff',border:'3px solid #2ecc71',color:'#1e7e34',padding:'14px 18px',borderRadius:12,boxShadow:'0 10px 30px rgba(0,0,0,.15)',maxWidth:420,textAlign:'center',fontWeight:600}}>
              <div style={{fontSize:20,marginBottom:4}}> {alertOk.barcode}</div>
                <div style={{fontSize:16}}>{alertOk.message}</div>
              </div>
            </div>
          )}
          <ToastContainer
            position="top-center"
            autoClose={4000}
            hideProgressBar
            newestOnTop
            closeOnClick
            pauseOnFocusLoss={false}
            draggable
            pauseOnHover={false}
          />



        {currentUser && (!showBottomNav || isUitscanner || isInscanning) && (
  <nav
    className={`${usesManagementTheme ? "management-nav" : ""} ${isUitscanner ? "grid grid-cols-3" : "flex"} bg-white ${
      (isUitscanner || isInscanning)
        ? unifiedScannerModeEnabled
          ? "fixed bottom-0 left-0 right-0 z-50 items-center justify-between gap-2 border-t px-3 pt-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] shadow-md"
          : "fixed bottom-0 left-0 right-0 z-50 items-center justify-between gap-3 border-t px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] shadow-md"
        : "mb-3 grid grid-cols-2 items-stretch gap-3 rounded p-3 text-center sm:flex sm:flex-wrap sm:items-center"
    }`}
  >
    {    (isUitscanner || isInscanning) ? (
      <>
        <div
          className={isUitscanner
            ? "contents"
            : `flex items-center gap-3 ${
                unifiedScannerModeEnabled ? "min-w-0 flex-1" : ""
              }`}
        >

          {!unifiedScannerModeEnabled && (
            <NavLink
              to="/"
              className={({ isActive }) =>
                `justify-self-center rounded px-3 py-1 font-semibold ${
                  isActive ? "bg-green-800 text-white" : "bg-green-400 text-white"
                }`
              }
            >
              {navLabels.home}
            </NavLink>
          )}

          {unifiedScannerModeEnabled && (
            <div
              className="inline-flex w-full max-w-md items-stretch rounded-lg border border-gray-300 bg-gray-100 p-1 shadow-inner"
              role="group"
              aria-label={it.scannerModeSelector}
            >
              <button
                type="button"
                onClick={() => setScannerMode(SCANNER_MODE_STANDARD)}
                className={`min-w-0 flex-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-bold transition-colors sm:px-3 sm:text-sm ${
                  !isInscannerPlusMode
                    ? "bg-green-800 text-white shadow"
                    : "text-gray-600 hover:bg-white"
                }`}
                aria-pressed={!isInscannerPlusMode}
              >
                {!isInscannerPlusMode && <span aria-hidden="true">✓ </span>}
                {it.scannerStandard}
                {!isInscannerPlusMode && (
                  <span className="ml-1 text-[10px] font-semibold uppercase opacity-80">
                    {it.scannerModeActive}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setScannerMode(SCANNER_MODE_PLUS)}
                className={`min-w-0 flex-1 whitespace-nowrap rounded-md px-2 py-1 text-xs font-bold transition-colors sm:px-3 sm:text-sm ${
                  isInscannerPlusMode
                    ? "bg-orange-600 text-white shadow"
                    : "text-gray-600 hover:bg-white"
                }`}
                aria-pressed={isInscannerPlusMode}
              >
                {isInscannerPlusMode && <span aria-hidden="true">✓ </span>}
                {it.scannerPlus}
                {isInscannerPlusMode && (
                  <span className="ml-1 text-[10px] font-semibold uppercase opacity-80">
                    {it.scannerModeActive}
                  </span>
                )}
              </button>
            </div>
          )}

          {isUitscanner && (
            <NavLink
              to="/voorraad-koppelen"
              className={({ isActive }) =>
                `justify-self-center rounded px-3 py-1 font-semibold ${
                  isActive ? "bg-green-800 text-white" : "bg-green-400 text-white"
                }`
              }
            >
              {navLabels.assignToOrder}
            </NavLink>
          )}
        </div>

        <div className={isUitscanner ? "contents" : "flex shrink-0 items-center gap-3"}>
          {!unifiedScannerModeEnabled && !isUitscanner && (
            <span className="text-sm text-gray-700">
              Ingelogd als: <strong>{currentUser.displayName}</strong>
            </span>
          )}

          <button
            onClick={logout}
            className="justify-self-center rounded bg-gray-200 px-3 py-1 font-semibold"
          >
            Uitloggen
          </button>
        </div>
      </>
    ) : (
      <>
        {usesManagementTheme && (
          <div className="management-brand" aria-label="Verdonk Broccoli Kistenvoorraad">
            <img src="/alleen_broccoli.png" alt="" aria-hidden="true" />
            <span>
              <small>Verdonk Broccoli</small>
              <strong>Kistenvoorraad</strong>
            </span>
          </div>
        )}
        <NavLink
          to="/"
          className={({ isActive }) =>
            `px-3 py-1 rounded font-semibold ${
              isActive ? "bg-green-800 text-white" : "bg-green-400 text-white"
            }`
          }
        >
          {navLabels.home}
        </NavLink>

        {!partijLockActive && currentUser && isPlanning && (
          <NavLink
            to="/voorraad-beheer"
            className={({ isActive }) =>
              `px-3 py-1 rounded font-semibold ${
                isActive ? "bg-green-800 text-white" : "bg-green-400 text-white"
              }`
            }
          >
            {navLabels.inventoryManagement}
          </NavLink>
        )}

        {!partijLockActive && currentUser && (canEdit || isPlanning) && (
          <NavLink
            to="/planning"
            className={({ isActive }) =>
              `px-3 py-1 rounded font-semibold ${
                isActive ? "bg-green-800 text-white" : "bg-green-400 text-white"
              }`
            }
          >
            {navLabels.planning}
          </NavLink>
        )}

        {!partijLockActive &&
          crateLookupScannerEnabled &&
          isPhoneDevice &&
          [ROLES.SUPERVISOR, ROLES.PLANNING, ROLES.ADMIN].includes(role) && (
            <NavLink
              to={APP_PATHS.CRATE_LOOKUP}
              className={({ isActive }) =>
                `px-3 py-1 rounded font-semibold ${
                  isActive ? "bg-green-800 text-white" : "bg-green-400 text-white"
                }`
              }
            >
              {navLabels.crateLookup}
            </NavLink>
          )}

        {!partijLockActive && currentUser?.role !== ROLES.UITSCANNER && (
          <NavLink
            to="/historie"
            className={({ isActive }) =>
              `px-3 py-1 rounded font-semibold ${
                isActive ? "bg-green-800 text-white" : "bg-green-400 text-white"
              }`
            }
          >
            {navLabels.history}
          </NavLink>
        )}

        {!partijLockActive && currentUser && canManageStockPages && (
          <NavLink
            to="/weekorders"
            className={({ isActive }) =>
              `px-3 py-1 rounded font-semibold ${
                isActive ? "bg-green-800 text-white" : "bg-green-400 text-white"
              }`
            }
          >
            {navLabels.weekOrders}
          </NavLink>
        )}

        {!partijLockActive && currentUser && canManageStockPages && (
          <NavLink
            to="/palletvoorraad"
            className={({ isActive }) =>
              `px-3 py-1 rounded font-semibold ${
                isActive ? "bg-green-800 text-white" : "bg-green-400 text-white"
              }`
            }
          >
            {navLabels.palletStock}
          </NavLink>
        )}

        {!partijLockActive && currentUser && canManageStockPages && (
          <NavLink
            to="/fustvoorraad"
            className={({ isActive }) =>
              `px-3 py-1 rounded font-semibold ${
                isActive ? "bg-green-800 text-white" : "bg-green-400 text-white"
              }`
            }
          >
            {navLabels.packagingStock}
          </NavLink>
        )}

        {!partijLockActive && currentUser && (canEdit || isPlanning) && (
          <NavLink
            to="/Order-data"
            className={({ isActive }) =>
              `px-3 py-1 rounded font-semibold ${
                isActive ? "bg-green-800 text-white" : "bg-green-400 text-white"
              }`
            }
          >
            {navLabels.orderData}
          </NavLink>
        )}

        {!partijLockActive && currentUser && (isSupervisor || isPlanning) && (
          <NavLink
            to="/percelen-beheer"
            className={({ isActive }) =>
              `px-3 py-1 rounded font-semibold ${
                isActive ? "bg-green-800 text-white" : "bg-green-400 text-white"
              }`
            }
          >
            {navLabels.fieldManagement}
          </NavLink>
        )}

        {currentUser && isAdmin && (
          <NavLink
            to="/kisten-beheer"
            className={({ isActive }) =>
              `px-3 py-1 rounded font-semibold ${
                isActive ? "bg-green-800 text-white" : "bg-green-400 text-white"
              }`
            }
          >
            {navLabels.crateManagement}
          </NavLink>
        )}

        {currentUser && (isSupervisor || isPlanning) && (
          <NavLink
            to="/houten-labels"
            className={({ isActive }) =>
              `px-3 py-1 rounded font-semibold ${
                isActive ? "bg-green-800 text-white" : "bg-green-400 text-white"
              }`
            }
          >
            {navLabels.woodenLabels}
          </NavLink>
        )}

        {!partijLockActive && currentUser && isAdmin && (
          <NavLink
            to="/users"
            className={({ isActive }) =>
              `px-3 py-1 rounded font-semibold ${
                isActive ? "bg-green-800 text-white" : "bg-green-400 text-white"
              }`
            }
          >
            {navLabels.users}
          </NavLink>
        )}

        {partijLockActive && (
          <span className="text-xs text-red-700 ml-2">
            {it.partyLockWarning}
          </span>
        )}
      </>
    )}
  </nav>
)}


      {currentUser && !isUitscanner && !isInscanning && (
      <div className={`${usesManagementTheme ? "management-accountbar" : ""} mb-6 p-3 rounded bg-white flex items-center gap-3`}>
        <span className="text-sm">
        {it.loggedInAs} <strong>{currentUser.displayName}</strong> ({currentUser.role})
        </span>

        <button
          onClick={logout}
          className="px-2 py-1 rounded bg-gray-200"
        >
          {it.logout}
        </button>

        <div
          ref={inscanLangRef}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <select
            value={inscanLang}
            onChange={(e) => setInscanLang(e.target.value)}
            className="border rounded px-2 py-1 text-xs bg-gray-200"
          >
            <option value="nl">Nederlands</option>
            <option value="en">English</option>
            <option value="pl">Polska</option>
          </select>
        </div>
      </div>
    )}


    <main className={usesManagementTheme ? "management-main" : undefined}>
    <AppErrorBoundary resetKey={Location.pathname}>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
          {!profileLoaded && !ssoChecking ? (
              <Route path="*" element={<div className="p-6">Laden...</div>} />
            ) : firebaseUser && !isAuthed ? (
              <Route path="*" element={<KistenAccessDeniedPage />} />
            ) : !isAuthed ? (
              <Route path="*" element={<LoginPage />} />
            ) : (
          
           <>
            <Route
              path={APP_PATHS.USERS}
              element={
             canRoleAccessPath(role, APP_PATHS.USERS)
              ? <UsersAdminPage showConfirm={showConfirm}/>
             : <div className="p-6">Geen toegang.</div>
             }
            />


            <Route
              path={APP_PATHS.HOME}
              element={<HomePage {...homePageProps} />}
            />

            <Route
              path={APP_PATHS.INVENTORY}
              element={
                canRoleAccessPath(role, APP_PATHS.INVENTORY)
                  ? <HomePage {...homePageProps} inventoryEditMode />
                  : <div className="p-6">Geen toegang.</div>
              }
            />

            <Route
              path={APP_PATHS.HISTORY}
              element={
                canRoleAccessPath(role, APP_PATHS.HISTORY)
                  ? <Historie gescandeKisten={gescandeKisten} kgPerKist={280} />
                  : <div className="p-6">Geen toegang.</div>
              }
            />
            <Route
              path={APP_PATHS.ORDER_DATA}
              element={
                canRoleAccessPath(role, APP_PATHS.ORDER_DATA)
                  ? <OrderdataPage />
                  : <div className="p-6">Geen toegang.</div>
              }
              />
            <Route
              path={APP_PATHS.WEEK_ORDERS}
              element={
                canRoleAccessPath(role, APP_PATHS.WEEK_ORDERS)
                  ? <WeekOrdersPage />
                  : <div className="p-6">Geen toegang.</div>
              }
            />
            <Route
              path={APP_PATHS.PALLET_STOCK}
              element={
                canRoleAccessPath(role, APP_PATHS.PALLET_STOCK)
                  ? <PalletVoorraadPage />
                  : <div className="p-6">Geen toegang.</div>
              }
            />
            <Route
              path={APP_PATHS.PALLET_ASSIGNMENT}
              element={
                canRoleAccessPath(role, APP_PATHS.PALLET_ASSIGNMENT)
                  ? <PalletAssignmentScannerPage />
                  : <div className="p-6">Geen toegang.</div>
              }
            />
            <Route
              path={APP_PATHS.FUST_STOCK}
              element={
                canRoleAccessPath(role, APP_PATHS.FUST_STOCK)
                  ? <FustVoorraadPage />
                  : <div className="p-6">Geen toegang.</div>
              }
            />
            <Route
              path={APP_PATHS.PLANNING}
              element={
                canRoleAccessPath(role, APP_PATHS.PLANNING)
                  ? <PlanningPage planningState={planningPageState} />
                  : <div className="p-6">Geen toegang.</div>
              }
            />
            <Route
               path={APP_PATHS.PARCELS}
                element={
                  canRoleAccessPath(role, APP_PATHS.PARCELS)
                    ? <PercelenbeheerPage showConfirm={showConfirm} />
                    : <div className="p-6">Geen toegang.</div>
                }
                />
                <Route
                  path={APP_PATHS.WOODEN_LABELS}
                   element={
                     canRoleAccessPath(role, APP_PATHS.WOODEN_LABELS)
                       ? <HoutenLabelsPage />
                       : <div className="p-6">Geen toegang.</div>
                  }
                />
                <Route
                  path={APP_PATHS.ALLOWED_CRATES}
                  element={
                    canRoleAccessPath(role, APP_PATHS.ALLOWED_CRATES)
                      ? <AllowedKistenPage showConfirm={showConfirm} />
                      : <div className="p-6">Geen toegang.</div>
                  }
                />
                <Route
                  path={APP_PATHS.CRATE_LOOKUP}
                  element={
                    crateLookupScannerEnabled &&
                    canRoleAccessPath(role, APP_PATHS.CRATE_LOOKUP)
                      ? (
                        <CrateLookupScannerPage
                          mode={canRoleUsePhoneInscanner(role) ? phoneScannerMode : "lookup"}
                          onModeChange={canRoleUsePhoneInscanner(role) ? setPhoneScannerMode : undefined}
                          partyActive={partijGestart}
                        >
                          <HomePage
                            {...homePageProps}
                            phoneScanMode
                            isInscanner
                            onManualModeChange={handlePhoneManualModeChange}
                            canEdit={false}
                            canRemoveCrates={false}
                            isPlanning={false}
                            magPlannen={false}
                            showBottomNav
                          />
                        </CrateLookupScannerPage>
                      )
                      : <div className="p-6">Geen toegang.</div>
                  }
                />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
      </Suspense>
      </AppErrorBoundary>
      </main>
    </div>
  
  
);

}

export default App;
